/**
 * Route preloading utilities for better navigation performance.
 * Preloads lazy-loaded page components on hover to reduce perceived load time.
 */

// Map of paths to their dynamic import functions
const routeModules: Record<string, () => Promise<unknown>> = {
  '/kalkulator': () => import('@/pages/CalculatorPage'),
  '/cennik': () => import('@/pages/CennikPage'),
  '/o-nas': () => import('@/pages/ONasPage'),
  '/faq': () => import('@/pages/FAQPage'),
  '/kontakt': () => import('@/pages/KontaktPage'),
  '/ochrana-sukromia': () => import('@/pages/OchranaSukromiaPage'),
  '/obchodne-podmienky': () => import('@/pages/ObchodnePodmienkyPage'),
  '/sluzby': () => import('@/pages/sluzby/SluzbyPage'),
  '/sluzby/uradne-preklady': () => import('@/pages/sluzby/UradnePrekladyPage'),
  '/sluzby/dopyt': () => import('@/pages/sluzby/LegalizaciaDopytPage'),
  '/sluzby/apostila': () => import('@/pages/sluzby/ApostilaPage'),
  '/sluzby/superlegalizacia': () => import('@/pages/sluzby/SuperlegalizaciaPage'),
  '/sluzby/zahranicne-dokumenty': () => import('@/pages/sluzby/ZahranicneDokumentyPage'),
  '/blog': () => import('@/pages/blog/BlogListPage'),
};

// Detail page patterns with their loaders
const detailPatterns: Array<{ pattern: RegExp; loader: () => Promise<unknown> }> = [
  { pattern: /^\/sluzby\/apostila\/.+/, loader: () => import('@/pages/sluzby/ApostilaDocumentDetailPage') },
  { pattern: /^\/sluzby\/zahranicne-dokumenty\/.+/, loader: () => import('@/pages/sluzby/ZahranicneDokumentyDetailPage') },
  { pattern: /^\/blog\/.+/, loader: () => import('@/pages/blog/BlogDetailPage') },
  // English
  { pattern: /^\/en\/services\/apostille\/.+/, loader: () => import('@/pages/sluzby/ApostilaDocumentDetailPage') },
  { pattern: /^\/en\/services\/foreign-documents\/.+/, loader: () => import('@/pages/sluzby/ZahranicneDokumentyDetailPage') },
  { pattern: /^\/en\/blog\/.+/, loader: () => import('@/pages/blog/BlogDetailPage') },
  // German
  { pattern: /^\/de\/dienstleistungen\/apostille\/.+/, loader: () => import('@/pages/sluzby/ApostilaDocumentDetailPage') },
  { pattern: /^\/de\/dienstleistungen\/auslaendische-dokumente\/.+/, loader: () => import('@/pages/sluzby/ZahranicneDokumentyDetailPage') },
  // Ukrainian
  { pattern: /^\/uk\/posluhy\/apostyl\/.+/, loader: () => import('@/pages/sluzby/ApostilaDocumentDetailPage') },
  { pattern: /^\/uk\/posluhy\/inozemni-dokumenty\/.+/, loader: () => import('@/pages/sluzby/ZahranicneDokumentyDetailPage') },
  { pattern: /^\/uk\/blog\/.+/, loader: () => import('@/pages/blog/BlogDetailPage') },
  // Russian
  { pattern: /^\/ru\/uslugi\/apostil\/.+/, loader: () => import('@/pages/sluzby/ApostilaDocumentDetailPage') },
  { pattern: /^\/ru\/uslugi\/inostrannye-dokumenty\/.+/, loader: () => import('@/pages/sluzby/ZahranicneDokumentyDetailPage') },
  // Spanish
  { pattern: /^\/es\/servicios\/apostilla\/.+/, loader: () => import('@/pages/sluzby/ApostilaDocumentDetailPage') },
  { pattern: /^\/es\/servicios\/documentos-extranjeros\/.+/, loader: () => import('@/pages/sluzby/ZahranicneDokumentyDetailPage') },
];

// Localized route mappings
const localizedMappings: Record<string, Record<string, string>> = {
  '/kalkulator': { en: '/calculator', de: '/rechner', uk: '/kalkulyator', ru: '/kalkulyator', es: '/calculadora' },
  '/cennik': { en: '/pricing', de: '/preise', uk: '/tsiny', ru: '/tseny', es: '/precios' },
  '/o-nas': { en: '/about-us', de: '/uber-uns', uk: '/pro-nas', ru: '/o-nas', es: '/sobre-nosotros' },
  '/faq': { en: '/faq', de: '/faq', uk: '/faq', ru: '/faq', es: '/faq' },
  '/kontakt': { en: '/contact', de: '/kontakt', uk: '/kontakt', ru: '/kontakt', es: '/contacto' },
  '/sluzby': { en: '/services', de: '/dienstleistungen', uk: '/posluhy', ru: '/uslugi', es: '/servicios' },
  '/sluzby/uradne-preklady': { en: '/services/official-translations', de: '/dienstleistungen/beglaubigte-ubersetzungen', uk: '/posluhy/ofitsiyni-pereklady', ru: '/uslugi/oficialnye-perevody', es: '/servicios/traducciones-oficiales' },
  '/sluzby/apostila': { en: '/services/apostille', de: '/dienstleistungen/apostille', uk: '/posluhy/apostyl', ru: '/uslugi/apostil', es: '/servicios/apostilla' },
  '/sluzby/superlegalizacia': { en: '/services/superlegalization', de: '/dienstleistungen/superlegalisation', uk: '/posluhy/superlegalizatsiya', ru: '/uslugi/superlegalizatsiya', es: '/servicios/superlegalizacion' },
  '/sluzby/zahranicne-dokumenty': { en: '/services/foreign-documents', de: '/dienstleistungen/auslaendische-dokumente', uk: '/posluhy/inozemni-dokumenty', ru: '/uslugi/inostrannye-dokumenty', es: '/servicios/documentos-extranjeros' },
  '/sluzby/dopyt': { en: '/services/inquiry', de: '/dienstleistungen/anfrage', uk: '/posluhy/zapyt', ru: '/uslugi/zapros', es: '/servicios/consulta' },
  '/blog': { en: '/blog', de: '/blog', uk: '/blog', ru: '/blog', es: '/blog' },
};

// Track already preloaded paths to avoid duplicate requests
const preloadedPaths = new Set<string>();

/**
 * Extracts the canonical (Slovak) path from a localized path
 */
function getCanonicalFromLocalized(path: string): string | null {
  // Check if path starts with language prefix
  const langMatch = path.match(/^\/(en|de|uk|ru|es)(\/.*)?$/);
  
  if (!langMatch) {
    // Already Slovak path
    return path;
  }
  
  const lang = langMatch[1];
  const restPath = langMatch[2] || '/';
  
  // Find the canonical path
  for (const [skPath, translations] of Object.entries(localizedMappings)) {
    if (translations[lang] === restPath) {
      return skPath;
    }
  }
  
  return null;
}

/**
 * Preloads the component for a given route path.
 * Handles both Slovak and localized paths.
 */
export function preloadRoute(path: string): void {
  // Skip if already preloaded or is homepage
  if (preloadedPaths.has(path) || path === '/' || path.match(/^\/(en|de|uk|ru|es)\/?$/)) {
    return;
  }
  
  // Try to find canonical path
  const canonicalPath = getCanonicalFromLocalized(path);
  
  // Check static routes first
  if (canonicalPath && routeModules[canonicalPath]) {
    preloadedPaths.add(path);
    routeModules[canonicalPath]();
    return;
  }
  
  // Check detail page patterns
  for (const { pattern, loader } of detailPatterns) {
    if (pattern.test(path)) {
      preloadedPaths.add(path);
      loader();
      return;
    }
  }
}

/**
 * Hook to create preload handler for link components
 */
export function createPreloadHandler(to: string) {
  return () => preloadRoute(to);
}
